import React, { useState } from "react";
import ReactApexChart from "react-apexcharts";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Divider,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useNavigate } from "react-router";

// ---- Dummy data (to be wired to APIs later) ----------------------------

const weekDays = [
  { day: "Mon", date: "05 May", count: 8 },
  { day: "Tue", date: "06 May", count: 12 },
  { day: "Wed", date: "07 May", count: 10 },
  { day: "Thu", date: "08 May", count: 15, active: true },
  { day: "Fri", date: "09 May", count: 11 },
  { day: "Sat", date: "10 May", count: 5 },
  { day: "Sun", date: "11 May", count: 3 },
];

const mattersToday = [
  {
    time: "10:00 AM",
    client: "ABC Pvt. Ltd.",
    matter: "CS/245/2024",
    court: "Delhi High Court",
    advocate: "R. Mehta",
    type: "Civil",
  },
  {
    time: "11:30 AM",
    client: "Sunrise Infra Ltd.",
    matter: "ARB/18/2024",
    court: "SIAC, Mumbai",
    advocate: "P. Sharma",
    type: "Arbitration",
  },
  {
    time: "02:00 PM",
    client: "Rajesh Kumar",
    matter: "CR/302/2023",
    court: "Tis Hazari Court",
    advocate: "A. Singh",
    type: "Criminal",
  },
  {
    time: "04:00 PM",
    client: "XYZ Developers",
    matter: "RERA/112/2024",
    court: "RERA, Gurugram",
    advocate: "N. Verma",
    type: "Real Estate",
  },
  {
    time: "05:30 PM",
    client: "Global Tech Ltd.",
    matter: "CS/678/2024",
    court: "NCLT, New Delhi",
    advocate: "R. Mehta",
    type: "Company",
  },
];

const clientStats = [
  {
    label: "Group of Companies",
    matters: 28,
    icon: <ApartmentOutlinedIcon />,
    color: "#5B6BF5",
  },
  {
    label: "Companies",
    matters: 56,
    icon: <BusinessOutlinedIcon />,
    color: "#5B6BF5",
  },
  {
    label: "Individuals",
    matters: 42,
    icon: <PersonOutlineOutlinedIcon />,
    color: "#5B6BF5",
  },
];

const clientTableRows = [
  {
    type: "Group of Companies",
    total: 28,
    active: 22,
    next7Days: 8,
    overdue: 3,
  },
  { type: "Companies", total: 56, active: 41, next7Days: 14, overdue: 6 },
  { type: "Individuals", total: 42, active: 31, next7Days: 10, overdue: 4 },
];
const clientTableTotal = clientTableRows.reduce(
  (acc, r) => ({
    total: acc.total + r.total,
    active: acc.active + r.active,
    next7Days: acc.next7Days + r.next7Days,
    overdue: acc.overdue + r.overdue,
  }),
  { total: 0, active: 0, next7Days: 0, overdue: 0 },
);

const caseTypes = [
  {
    name: "Arbitration",
    color: "#2F5BEA",
    total: 38,
    breakup: [
      { label: "Active", value: 30 },
      { label: "Closed", value: 6 },
      { label: "Stayed", value: 2 },
    ],
    allDocuments: 12,
    nextDate: 7,
    actionsDue: 5,
  },
  {
    name: "Criminal",
    color: "#2FAE6B",
    total: 27,
    breakup: [
      { label: "Active", value: 21 },
      { label: "Closed", value: 4 },
      { label: "Pending", value: 2 },
    ],
    allDocuments: 9,
    nextDate: 5,
    actionsDue: 3,
  },
  {
    name: "Real Estate",
    color: "#8A4FE0",
    total: 35,
    breakup: [
      { label: "Active", value: 26 },
      { label: "Closed", value: 6 },
      { label: "Pending", value: 3 },
    ],
    allDocuments: 14,
    nextDate: 8,
    actionsDue: 6,
  },
];

const financeTabs = [
  "MTD (May 2025)",
  "YTD (Apr 2025 - Mar 2026)",
  "Client Wise",
];

const financeStats = [
  {
    label: "Invoice Amount",
    value: "₹ 18,45,000",
    change: "+12.5% vs Apr 2025",
  },
  {
    label: "Collection Amount",
    value: "₹ 12,35,000",
    change: "+15.8% vs Apr 2025",
  },
  { label: "Collection %", value: "66.98%", change: "+2.1% vs Apr 2025" },
];

const financeClientRows = [
  {
    client: "ABC Pvt. Ltd.",
    invoice: "4,25,000",
    collection: "3,10,000",
    collectionPct: "72.94%",
  },
  {
    client: "Sunrise Infra Ltd.",
    invoice: "3,75,000",
    collection: "2,40,000",
    collectionPct: "64.00%",
  },
  {
    client: "XYZ Developers",
    invoice: "2,80,000",
    collection: "2,00,000",
    collectionPct: "71.43%",
  },
  {
    client: "Rajesh Kumar",
    invoice: "1,25,000",
    collection: "75,000",
    collectionPct: "60.00%",
  },
  {
    client: "Global Tech Ltd.",
    invoice: "1,20,000",
    collection: "70,000",
    collectionPct: "58.33%",
  },
];
const financeClientTotal = {
  invoice: "18,45,000",
  collection: "12,35,000",
  collectionPct: "66.98%",
};

// ---- Small building blocks ----------------------------------------------

function SectionCard({ children, sx = {} }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        borderRadius: 1,
        border: "1px solid #ECEFF3",
        height: "100%",
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

const denseTableSx = {
  "& .MuiTableCell-root": {
    padding: "2px 5px",
    fontSize: 10.5,
    lineHeight: 1.25,
  },
};

function CaseRing({ color, total, active }) {
  const pct = Math.round((active / total) * 100);
  const options = {
    chart: { sparkline: { enabled: true } },
    colors: [color],
    plotOptions: {
      radialBar: {
        hollow: { size: "48%" },
        track: { background: "#F0F1F6" },
        dataLabels: { show: false },
      },
    },
    stroke: { lineCap: "round" },
  };

  return (
    <Box sx={{ position: "relative", width: 84, height: 84, mx: "auto" }}>
      <ReactApexChart
        options={options}
        series={[pct]}
        type="radialBar"
        height={84}
        width={84}
      />
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1 }}>
          {total}
        </Typography>
        <Typography sx={{ fontSize: 8.5, color: "text.secondary" }}>
          Matters
        </Typography>
      </Box>
    </Box>
  );
}

function CaseTypeCard({ caseType }) {
  return (
    <SectionCard sx={{ textAlign: "center", p: 0.75 }}>
      <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: 12 }}>
        {caseType.name}
      </Typography>
      <CaseRing
        color={caseType.color}
        total={caseType.total}
        active={caseType.breakup[0].value}
      />
      <Box sx={{ mt: 0.5, mb: 0.5 }}>
        {caseType.breakup.map((b, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              fontSize: 10,
              color: "text.secondary",
              lineHeight: 1.35,
            }}
          >
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                bgcolor: caseType.color,
              }}
            />
            {b.label} : {b.value}
          </Box>
        ))}
      </Box>
      <Divider sx={{ mb: 0.5 }} />
      <Grid container>
        <Grid item xs={4}>
          <DescriptionOutlinedIcon sx={{ fontSize: 13 }} color="action" />
          <Typography sx={{ fontWeight: 700, fontSize: 11 }}>
            {caseType.allDocuments}
          </Typography>
          <Typography sx={{ fontSize: 7.5, color: "text.secondary" }}>
            All Documents
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <EventOutlinedIcon sx={{ fontSize: 13 }} color="action" />
          <Typography sx={{ fontWeight: 700, fontSize: 11 }}>
            {caseType.nextDate}
          </Typography>
          <Typography sx={{ fontSize: 7.5, color: "text.secondary" }}>
            Next Date
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <WarningAmberOutlinedIcon sx={{ fontSize: 13 }} color="action" />
          <Typography sx={{ fontWeight: 700, fontSize: 11 }}>
            {caseType.actionsDue}
          </Typography>
          <Typography sx={{ fontSize: 7.5, color: "text.secondary" }}>
            Actions Due
          </Typography>
        </Grid>
      </Grid>
      <Typography
        sx={{
          mt: 0.5,
          fontSize: 10,
          color: "primary.main",
          cursor: "pointer",
        }}
      >
        View Details
      </Typography>
    </SectionCard>
  );
}

export default function LegalDashboard() {
  const [financeTab, setFinanceTab] = useState(0);
  const navigate = useNavigate();

  return (
    <Box sx={{ p: 0 }}>
      {/* Page header */}
      <Grid container spacing={1.25}>
        {/* Dates for This Week */}
        <Grid item xs={12} md={8}>
          <SectionCard>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 14 }}>
                Dates for This Week
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  border: "1px solid #ECEFF3",
                  borderRadius: 1,
                  px: 1,
                  py: 0.1,
                }}
              >
                <IconButton size="small">
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <CalendarMonthIcon
                  fontSize="small"
                  sx={{ color: "text.secondary" }}
                />
                <Typography sx={{ fontSize: 12, mx: 1 }}>
                  05 May - 11 May 2025 (This Week)
                </Typography>
                <IconButton size="small">
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Grid container spacing={0.75} sx={{ mb: 1 }}>
              {weekDays.map((d, i) => (
                <Grid item xs={12 / 7} key={i}>
                  <Box
                    sx={{
                      textAlign: "center",
                      borderRadius: 0.75,
                      py: 0.5,
                      bgcolor: d.active ? "primary.main" : "#F5F7FA",
                      color: d.active ? "#ffffff" : "text.primary",
                    }}
                  >
                    <Typography
                      sx={{
                        color: d.active ? "#ffffff" : "text.primary",
                        fontSize: 9.5,
                      }}
                    >
                      {d.day}
                    </Typography>
                    <Typography
                      sx={{
                        color: d.active ? "#ffffff" : "text.primary",
                        fontSize: 9.5,
                      }}
                    >
                      {d.date}
                    </Typography>
                    <Typography
                      sx={{
                        color: d.active ? "#ffffff" : "text.primary",
                        fontWeight: 700,
                        fontSize: 14,
                      }}
                    >
                      {d.count}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: 12.5 }}>
                Matters on Thu, 08 May 2025 (15)
              </Typography>
              <Typography
                onClick={() => {
                  navigate("/legal/calendar");
                }}
                sx={{
                  fontSize: 11,
                  color: "primary.main",
                  cursor: "pointer",
                }}
              >
                View Calendar
              </Typography>
            </Box>

            <Table size="small" sx={denseTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Case / Matter</TableCell>
                  <TableCell>Court</TableCell>
                  <TableCell>Advocate</TableCell>
                  <TableCell>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mattersToday.map((m, i) => (
                  <TableRow key={i}>
                    <TableCell>{m.time}</TableCell>
                    <TableCell>{m.client}</TableCell>
                    <TableCell>{m.matter}</TableCell>
                    <TableCell>{m.court}</TableCell>
                    <TableCell>{m.advocate}</TableCell>
                    <TableCell>{m.type}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography
              sx={{
                textAlign: "center",
                mt: 0.75,
                fontSize: 11,
                color: "primary.main",
                cursor: "pointer",
              }}
            >
              View All (15)
            </Typography>
          </SectionCard>
        </Grid>

        {/* By Clients */}
        <Grid item xs={12} md={4}>
          <SectionCard>
            <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 14 }}>
              By Clients
            </Typography>
            <Grid container spacing={0.75} sx={{ mb: 1 }}>
              {clientStats.map((c, i) => (
                <Grid item xs={4} key={i}>
                  <Box
                    sx={{
                      textAlign: "center",
                      border: "1px solid #ECEFF3",
                      borderRadius: 0.75,
                      py: 0.75,
                      px: 0.5,
                    }}
                  >
                    <Box sx={{ color: c.color, mb: 0.25, fontSize: 18 }}>
                      {c.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                      {c.matters}
                    </Typography>
                    <Typography sx={{ fontSize: 9, color: "text.secondary" }}>
                      Matters
                    </Typography>
                  </Box>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Box
                  sx={{
                    textAlign: "center",
                    borderRadius: 0.75,
                    py: 0.5,
                    bgcolor: "#F5F7FA",
                  }}
                >
                  <Typography sx={{ fontSize: 9.5, color: "text.secondary" }}>
                    Total Matters
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                    {clientTableTotal.total}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Table size="small" sx={denseTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Client Type</TableCell>
                  <TableCell align="right">Total Matters</TableCell>
                  <TableCell align="right">Active Matters</TableCell>
                  <TableCell align="right">Next 7 Days</TableCell>
                  <TableCell align="right">Overdue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clientTableRows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.type}</TableCell>
                    <TableCell align="right">{r.total}</TableCell>
                    <TableCell align="right">{r.active}</TableCell>
                    <TableCell align="right">{r.next7Days}</TableCell>
                    <TableCell align="right">{r.overdue}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {clientTableTotal.total}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {clientTableTotal.active}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {clientTableTotal.next7Days}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {clientTableTotal.overdue}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Typography
              sx={{
                textAlign: "center",
                mt: 0.75,
                fontSize: 11,
                color: "primary.main",
                cursor: "pointer",
              }}
            >
              View Client Wise Details
            </Typography>
          </SectionCard>
        </Grid>

        {/* By Nature of Cases */}
        <Grid item xs={12} md={8}>
          <SectionCard>
            <Typography sx={{ fontWeight: 600, mb: 0.75, fontSize: 14 }}>
              By Nature of Cases
            </Typography>
            <Grid container spacing={1.25}>
              {caseTypes.map((c, i) => (
                <Grid item xs={12} sm={4} key={i}>
                  <CaseTypeCard caseType={c} />
                </Grid>
              ))}
            </Grid>
            <Typography
              sx={{
                textAlign: "center",
                mt: 0.75,
                fontSize: 11,
                color: "primary.main",
                cursor: "pointer",
              }}
            >
              {/* View All Case Types */}
            </Typography>
          </SectionCard>
        </Grid>

        {/* Finance */}
        <Grid item xs={12} md={4}>
          <SectionCard>
            <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 14 }}>
              Finance
            </Typography>

            <Box sx={{ display: "flex", gap: 0.5, mb: 1, flexWrap: "wrap" }}>
              {financeTabs.map((tab, i) => (
                <Chip
                  key={i}
                  label={tab}
                  onClick={() => setFinanceTab(i)}
                  color={financeTab === i ? "primary" : "default"}
                  variant={financeTab === i ? "filled" : "outlined"}
                  size="small"
                  sx={{ fontSize: 10.5, height: 22 }}
                />
              ))}
            </Box>

            <Grid container spacing={0.75} sx={{ mb: 1 }}>
              {financeStats.map((f, i) => (
                <Grid item xs={4} key={i}>
                  <Box
                    sx={{
                      textAlign: "center",
                      border: "1px solid #ECEFF3",
                      borderRadius: 0.75,
                      py: 0.5,
                      px: 0.5,
                    }}
                  >
                    <Typography sx={{ fontSize: 9, color: "text.secondary" }}>
                      {f.label}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                      {f.value}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.25,
                        color: "success.main",
                      }}
                    >
                      <TrendingUpIcon sx={{ fontSize: 10 }} />
                      <Typography sx={{ fontSize: 8 }}>{f.change}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Typography sx={{ fontWeight: 600, fontSize: 11.5, mb: 0.5 }}>
              MTD - Client Wise Summary
            </Typography>
            <Table size="small" sx={denseTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell align="right">Invoice (₹)</TableCell>
                  <TableCell align="right">Collection (₹)</TableCell>
                  <TableCell align="right">Collection %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {financeClientRows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.client}</TableCell>
                    <TableCell align="right">{r.invoice}</TableCell>
                    <TableCell align="right">{r.collection}</TableCell>
                    <TableCell align="right">{r.collectionPct}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {financeClientTotal.invoice}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {financeClientTotal.collection}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {financeClientTotal.collectionPct}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Typography
              sx={{
                textAlign: "center",
                mt: 0.75,
                fontSize: 11,
                color: "primary.main",
                cursor: "pointer",
              }}
            >
              View Full Finance Report
            </Typography>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
